import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { selectUser, selectIsLoggedIn, selectUserName } from "../../store/userSlice";
import { 
  selectAdminClubIds, 
  selectPermissionsLoaded, 
  selectPermissionsLoading,
  fetchUserPermissions 
} from "../../store/permissionsSlice";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import ClubPortalModal from "../ClubPortalModal";

function MobileTopNavbar({ 
  onLogout,
  onOpenSignIn,
  onOpenSignUp,
  onOpenSettings 
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useAppSelector(selectUser);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const userName = useAppSelector(selectUserName);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showClubPortal, setShowClubPortal] = useState(false);
  
  // Get permissions from Redux
  const adminClubIds = useAppSelector(selectAdminClubIds);
  const permissionsLoaded = useAppSelector(selectPermissionsLoaded);
  const permissionsLoading = useAppSelector(selectPermissionsLoading);

  useEffect(() => {
    if (isLoggedIn && user?.id && !permissionsLoaded && !permissionsLoading) {
      dispatch(fetchUserPermissions(user.id));
    }
  }, [isLoggedIn, user?.id, dispatch]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action) => {
    handleMenuClose();
    action();
  };

  // Only render on mobile
  if (!isMobile) {
    return null;
  }

  return (
    <>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            San Diego Gaming
          </Typography>
          
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            aria-label="Open user menu"
          >
            {isLoggedIn ? (
              <Avatar
                sx={{ width: 32, height: 32 }}
                src={user?.avatar_url}
              >
                {userName.charAt(0)}
              </Avatar>
            ) : (
              <MenuIcon />
            )}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {isLoggedIn ? (
          <>
            <MenuItem onClick={() => handleMenuItemClick(() => navigate('/dashboard'))}>
              <DashboardIcon sx={{ mr: 1 }} />
              Dashboard
            </MenuItem>
            
            {adminClubIds.length > 0 && (
              <MenuItem onClick={() => handleMenuItemClick(() => setShowClubPortal(true))}>
                <GroupIcon sx={{ mr: 1 }} />
                Club Portal
              </MenuItem>
            )}
            
            {user?.role?.name === 'admin' && (
              <MenuItem onClick={() => handleMenuItemClick(() => navigate('/admin'))}>
                <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                Admin Console
              </MenuItem>
            )}
            
            <MenuItem onClick={() => handleMenuItemClick(onOpenSettings)}>
              <SettingsIcon sx={{ mr: 1 }} />
              Settings
            </MenuItem>
            
            <Divider />
            <MenuItem onClick={() => handleMenuItemClick(onLogout)}>
              <LogoutIcon sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => handleMenuItemClick(onOpenSignIn)}>
              <LoginIcon sx={{ mr: 1 }} />
              Sign In
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick(onOpenSignUp)}>
              <PersonAddIcon sx={{ mr: 1 }} />
              Sign Up
            </MenuItem>
          </>
        )}
      </Menu>

      <ClubPortalModal
        open={showClubPortal}
        onClose={() => setShowClubPortal(false)}
        adminClubIds={adminClubIds}
        onEditClub={clubId => {
          setShowClubPortal(false);
          navigate(`/clubs/${clubId}/edit`);
        }}
      />
    </>
  );
}

export default MobileTopNavbar;
